import {useContext, useMemo} from 'react';

import {useParams} from 'react-router-dom';

import CloseIcon from '@mui/icons-material/Close';
import {Grid, IconButton} from '@mui/material';

import EmpionDialog from '../../../components/EmpionDialog/EmpionDialog';
import {ErrorMessage} from '../../../components/ErrorMessage/ErrorMessage';
import {Loading} from '../../../components/Loading/Loading';
import {LoadingContainer} from '../../../components/Loading/LoadingContainer';
import {RejectionReasonModal} from '../../../components/RejectionReasonModal/RejectionReasonModal';
import {LanguageContext} from '../../../contexts/LanguageContext/LanguageContext';
import {UserContext} from '../../../contexts/UserContext/UserContext';
import {CompanyInfoToggle} from '../../../features/applicant/matches/components/CompanyInfoToggle/CompanyInfoToggle';
import {CultureFitDetails} from '../../../features/applicant/matches/components/CultureFitDetails/CultureFitDetails';
import {MatchDetailsHeader} from '../../../features/applicant/matches/components/MatchDetailsHeader/MatchDetailsHeader';
import {useApplicantMatchesPage} from '../../../features/applicant/matches/hooks/useApplicantMatchesPage';
import {useResizeObserver} from '../../../hooks/useResizeObserver';
import {empionGreen} from '../../../layouts/theme/EmpionColors';
import {ApplicantRoleData} from '../../../types/User';

export const ApplicantMatchDetailsPage = () => {
  const params = useParams<{id: string}>();
  const {currentUser} = useContext(UserContext);
  const lang = useContext(LanguageContext);

  const applicantId = (currentUser?.role.data as ApplicantRoleData).applicantId;

  const {
    jobPosting,
    isJobLoading,
    companyProfile,
    match,
    isDataLoading,
    jobPostingError,
    matchError,
    companyError,
    acceptMatch,
    rejectMatch,
    isUpdatingMatch,
    rejectMatchError,
    setIsModalOpen,
    isModalOpen,
    cultureInformation,
    cultureInfoError,
  } = useApplicantMatchesPage(params.id || '', applicantId);

  const {refCallback, dimensions, offsetTop} = useResizeObserver();
  const topOffset = useMemo(() => (dimensions?.height || 0) + (offsetTop || 0), [dimensions?.height, offsetTop]);

  const onClickHandler = (isInterested?: boolean) => {
    if (!isInterested) {
      setIsModalOpen(true);
    } else {
      acceptMatch();
    }
  };

  if (jobPostingError || companyError || matchError || cultureInfoError) {
    return <ErrorMessage />;
  }

  if (isDataLoading || !jobPosting || !companyProfile || !match) {
    return <Loading backDrop />;
  }

  const applicantCultureType = cultureInformation
    ? {name: cultureInformation.cultureType.name[lang], url: cultureInformation.cultureType.imageUrl}
    : null;

  return (
    <>
      {isJobLoading ? (
        <LoadingContainer minHeight='400px' />
      ) : (
        <>
          <MatchDetailsHeader onClick={onClickHandler} match={match} />
          <CompanyInfoToggle
            elementRef={refCallback}
            jobPosting={jobPosting}
            companyProfile={companyProfile}
            locations={match.companyJobPosting.locations}
          />
          {!!match.company.cultureType && (
            <CultureFitDetails
              applicantCultureType={applicantCultureType}
              applicantProfileImg={(currentUser?.role?.data as ApplicantRoleData)?.avatarUrl || ''}
              match={match}
              siblingOffset={topOffset}
            />
          )}
          <EmpionDialog
            open={isModalOpen}
            onClose={() => {
              setIsModalOpen(false);
            }}
          >
            <Grid container justifyContent={'flex-end'}>
              <IconButton
                onClick={() => {
                  setIsModalOpen(false);
                }}
              >
                <CloseIcon sx={{color: empionGreen[700]}} />
              </IconButton>
            </Grid>
            <RejectionReasonModal
              rejectionType='applicant'
              onReject={rejectMatch}
              isUpdatingMatch={isUpdatingMatch}
              error={rejectMatchError}
            />
          </EmpionDialog>
        </>
      )}
    </>
  );
};
